import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import Paginator from '../components/Paginator';
import ConfirmDialog from '../components/ConfirmDialog';
import useBackButton from '../hooks/useBackButton';
import styles from './AdminUsersTab.module.css';

export default function AdminUsersTab({ onToast }) {
  const { token, user: currentUser } = useAuth();
  const { t, lang } = useLang();
  
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Advanced filters and sorting state
  const [filterRole, setFilterRole] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Edit modal state
  const [modal, setModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ role: 'user', plan_id: 1, is_active: true });
  const [saving, setSaving] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    onConfirm: null,
    type: 'warning'
  });

  async function loadData() {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [uRes, pRes] = await Promise.all([
        fetch(`${API}/api/users`, { headers }),
        fetch(`${API}/api/plans/all`, { headers })
      ]);
      if (uRes.ok) setUsers(await uRes.json());
      if (pRes.ok) setPlans(await pRes.json());
    } catch (e) {
      console.error(e);
      onToast(t('common.error') || 'Failed to load user list');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line

  function triggerBan(u) {
    if (u.id === 0 || u.email === 'admin@qeduvo.com') {
      onToast('Cannot ban the super-admin account');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: u.is_active ? 'Ban Account' : 'Unban Account',
      message: `Are you sure you want to ${u.is_active ? 'ban' : 'unban'} ${u.display_name}? This will affect their system access status.`,
      confirmText: u.is_active ? 'Ban' : 'Unban',
      cancelText: t('common.cancel', 'Cancel'),
      type: 'warning',
      onConfirm: () => confirmToggleBan(u)
    });
  }

  async function confirmToggleBan(u) {
    try {
      const nextStatus = !u.is_active;
      const res = await fetch(`${API}/api/users/${u.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: nextStatus })
      });

      if (res.ok) {
        onToast(t('common.success') || 'User status updated');
        await loadData();
      } else {
        const errorData = await res.json();
        onToast(errorData.error || t('common.error'));
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error'));
    } finally {
      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    }
  }

  function triggerDelete(u) {
    if (u.id === 0 || u.email === 'admin@qeduvo.com') {
      onToast('Cannot delete the super-admin account');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Permanently Delete User',
      message: `Are you absolutely sure you want to delete ${u.display_name}? This action is permanent and will delete all their posts, memberships, and progress.`,
      confirmText: 'Delete',
      cancelText: t('common.cancel', 'Cancel'),
      type: 'danger',
      onConfirm: () => confirmDeleteUser(u)
    });
  }

  async function confirmDeleteUser(u) {
    try {
      const res = await fetch(`${API}/api/users/${u.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        onToast(t('common.success') || 'User deleted successfully');
        await loadData();
      } else {
        const errorData = await res.json();
        onToast(errorData.error || t('common.error'));
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error'));
    } finally {
      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    }
  }

  function openEdit(u) {
    setEditingUser(u);
    setForm({
      role: u.role || 'user',
      plan_id: u.plan_id || 1,
      is_active: u.is_active !== false
    });
    setModal(true);
  }

  function closeModal() {
    setModal(false);
    setEditingUser(null);
  }

  useBackButton(closeModal, modal);

  async function saveUser(e) {
    e.preventDefault();
    if (!editingUser) return;
    setSaving(true);
    
    try {
      const res = await fetch(`${API}/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          role: form.role,
          plan_id: Number(form.plan_id),
          is_active: form.is_active
        })
      });

      if (res.ok) {
        onToast(t('common.success') || 'User configuration updated');
        closeModal();
        await loadData();
      } else {
        const errorData = await res.json();
        onToast(errorData.error || t('common.error'));
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  }

  // Filter logic
  const filtered = users.filter(u => {
    const matchesSearch = !search ||
      u.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
      
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    
    const matchesPlan = filterPlan === 'all' || 
      (filterPlan === 'free' ? !u.plan_id || u.plan_id === 1 : String(u.plan_id) === filterPlan);
      
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' ? u.is_active === true : u.is_active === false);

    return matchesSearch && matchesRole && matchesPlan && matchesStatus;
  });

  // Sort logic
  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortKey) {
      case 'display_name':
        aVal = (a.display_name || '').toLowerCase();
        bVal = (b.display_name || '').toLowerCase();
        break;
      case 'role':
        aVal = a.role || '';
        bVal = b.role || '';
        break;
      case 'plan':
        aVal = a.plan_name || '';
        bVal = b.plan_name || '';
        break;
      case 'level':
        aVal = a.level || 1;
        bVal = b.level || 1;
        break;
      case 'is_active':
        aVal = a.is_active ? 1 : 0;
        bVal = b.is_active ? 1 : 0;
        break;
      case 'created_at':
      default:
        aVal = new Date(a.created_at).getTime();
        bVal = new Date(b.created_at).getTime();
        break;
    }

    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate logic
  const paginated = sorted.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  function renderSortIndicator(key) {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  return (
    <div className="flex-col">
      <div className="card">
        <div className="card-header">
          <div className="card-title">👥 User Management Console</div>
        </div>
        <div className="card-body" style={{ paddingBottom: 16 }}>
          <div className={styles.searchBar}>
            <input 
              value={search} 
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} 
              placeholder="Search by display name or email address..." 
              className={styles.searchInput}
            />
            <button className="btn btn-ghost btn-sm" onClick={loadData}>🔄 Refresh</button>
          </div>
          
          <div className={styles.filterBar}>
            <select
              className={styles.filterSelect}
              value={filterRole}
              onChange={e => { setFilterRole(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All Roles</option>
              <option value="user">Standard User</option>
              <option value="admin">Administrator</option>
            </select>

            <select
              className={styles.filterSelect}
              value={filterPlan}
              onChange={e => { setFilterPlan(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All Plan Tiers</option>
              {plans.map(p => (
                <option key={p.id} value={String(p.id)}>
                  {lang === 'th' ? p.name_th : p.name}
                </option>
              ))}
            </select>

            <select
              className={styles.filterSelect}
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="banned">Banned</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>{t('common.loading')}</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.sortHeader} onClick={() => handleSort('display_name')}>
                      User Details{renderSortIndicator('display_name')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('role')}>
                      Role{renderSortIndicator('role')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('plan')}>
                      Plan Tier{renderSortIndicator('plan')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('level')}>
                      Level & XP{renderSortIndicator('level')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('is_active')}>
                      Status{renderSortIndicator('is_active')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('created_at')}>
                      Joined Date{renderSortIndicator('created_at')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(u => {
                    const isSelf = u.id === currentUser?.id || u.email === currentUser?.email;
                    const isSuper = u.id === 0 || u.email === 'admin@qeduvo.com';
                    
                    return (
                      <tr key={u.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <img 
                              src={u.avatar_url ? `${API}${u.avatar_url}` : `https://api.dicebear.com/7.x/initials/svg?seed=${u.display_name}`} 
                              alt="avatar" 
                              style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', background: 'var(--surface2)' }} 
                            />
                            <div>
                              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                                {u.display_name} {isSelf && <span style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600 }}>(You)</span>}
                              </div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`${styles.roleTag} ${u.role === 'admin' ? styles.admin : styles.user}`}>
                            {u.role}
                          </span>
                        </td>
                        <td>
                          <span className={styles.planTag}>{u.plan_name || 'Free'}</span>
                        </td>
                        <td>
                          <div style={{ fontSize: 12, fontWeight: 500 }}>Lvl {u.level || 1}</div>
                          <div className="text-muted" style={{ fontSize: 10 }}>{u.xp || 0} XP</div>
                        </td>
                        <td>
                          <span className={`${styles.statusBadge} ${u.is_active ? styles.active : styles.banned}`}>
                            <span className={styles.statusDot} />
                            {u.is_active ? 'Active' : 'Banned'}
                          </span>
                        </td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button 
                              className="btn btn-ghost btn-sm" 
                              onClick={() => openEdit(u)}
                              disabled={isSuper}
                            >
                              ✏️ Edit
                            </button>
                            <button 
                              className="btn btn-ghost btn-sm" 
                              style={{ color: u.is_active ? 'var(--accent2)' : 'var(--accent3)' }}
                              onClick={() => triggerBan(u)}
                              disabled={isSuper}
                            >
                              {u.is_active ? 'Ban' : 'Unban'}
                            </button>
                            <button 
                              className="btn btn-danger btn-sm" 
                              onClick={() => triggerDelete(u)}
                              disabled={isSuper}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {paginated.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>
                        No matching users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Paginator
              totalItems={sorted.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      {/* Edit User Modal */}
      {modal && editingUser && (
        <div className={styles.modalOverlay} onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className={styles.modal} style={{ width: 'min(480px, 100%)' }}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>Configure User: {editingUser.display_name}</div>
              <button className={styles.modalClose} onClick={closeModal}>×</button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={saveUser}>
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">System Access Role</label>
                  <select 
                    className="form-input" 
                    value={form.role} 
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  >
                    <option value="user">USER (Standard Member Access)</option>
                    <option value="admin">ADMIN (Super Administrator)</option>
                  </select>
                </div>
                
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Subscription Tier Plan</label>
                  <select 
                    className="form-input" 
                    value={form.plan_id} 
                    onChange={e => setForm(f => ({ ...f, plan_id: Number(e.target.value) }))}
                  >
                    {plans.map(p => (
                      <option key={p.id} value={p.id}>
                        {lang === 'th' ? p.name_th : p.name} (฿{p.price_monthly.toLocaleString()}/mo)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label className="form-label">Account Active State</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={form.is_active} 
                      onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} 
                      style={{ width: 18, height: 18 }} 
                    />
                    <span style={{ fontSize: 13, color: 'var(--text2)' }}>Allow this user to sign in to Qeduvo</span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button type="button" className="btn btn-ghost" onClick={closeModal}>{t('common.cancel')}</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? t('common.loading') : 'Save Configuration'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reusable Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        type={confirmDialog.type}
      />
    </div>
  );
}
